import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  TextField,
  Chip,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import ExportModal from "../components/chartmaker/ExportModal";
import {
  compilePattern,
  compilePatternToText,
} from "../services/patternCompiler";
import SEO from "../components/common/SEO";
import "./Gallery.css";

const Gallery = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [allRows, setAllRows] = useState([]);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportData, setExportData] = useState({
    chart: null,
    title: "",
    compiled: "",
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "error" });

  const CSV_URL =
    "https://docs.google.com/spreadsheets/d/e/2PACX-1vREzRocc1BHh5Rzq_6R9T2ODbIMRNME6K2b04e8JP1w3M--Xu7t2Y65fCWa2ryiffvPQx2cf2zeOLpj/pub?gid=727427832&single=true&output=csv";

  function parseCsv(text) {
    const rows = [];
    let current = [];
    let value = "";
    let inQuotes = false;

    for (let i = 0; i < text.length; i += 1) {
      const char = text[i];
      const next = text[i + 1];

      if (inQuotes) {
        if (char === '"' && next === '"') {
          value += '"';
          i += 1; // skip escaped quote
        } else if (char === '"') {
          inQuotes = false;
        } else {
          value += char;
        }
      } else {
        if (char === '"') {
          inQuotes = true;
        } else if (char === ",") {
          current.push(value);
          value = "";
        } else if (char === "\n") {
          current.push(value);
          rows.push(current);
          current = [];
          value = "";
        } else if (char === "\r") {
          // ignore CR, handle CRLF by letting \n branch push row
        } else {
          value += char;
        }
      }
    }

    // flush last value
    if (value.length > 0 || current.length > 0) {
      current.push(value);
      rows.push(current);
    }

    if (rows.length === 0) return [];
    const headers = rows[0].map((h) => h.trim());
    const records = rows.slice(1).map((r) => {
      const obj = {};
      headers.forEach((h, idx) => {
        obj[h] = (r[idx] || "").trim();
      });
      return obj;
    });
    return records;
  }

  function base64ToUtf8(b64) {
    try {
      return decodeURIComponent(escape(window.atob(b64)));
    } catch (e) {
      try {
        return window.atob(b64);
      } catch (err) {
        return "";
      }
    }
  }

  function simpleHash(str) {
    let h = 5381;
    for (let i = 0; i < str.length; i += 1)
      h = ((h << 5) + h) ^ str.charCodeAt(i);
    return (h >>> 0).toString(16);
  }

  function generateChartThumbnailSvg(chartObj, options = {}) {
    const gap = options.gap || 1; // grid line
    const pad = options.pad || 6; // padding around grid
    const fontSize = options.fontSize || 8;
    const maxSize = options.maxSize || 192; // target bounding box in px

    const w = chartObj?.width || 0;
    const h = chartObj?.height || 0;
    // compute cell to fit within maxSize (preserve aspect ratio, include gaps and padding)
    const availableW = Math.max(
      1,
      maxSize - pad * 2 - Math.max(0, w - 1) * gap,
    );
    const availableH = Math.max(
      1,
      maxSize - pad * 2 - Math.max(0, h - 1) * gap,
    );
    const cellX = w > 0 ? Math.floor(availableW / Math.max(1, w)) : 10;
    const cellY = h > 0 ? Math.floor(availableH / Math.max(1, h)) : 10;
    const cell = Math.max(6, Math.min(cellX, cellY));

    const widthPx = pad * 2 + w * cell + Math.max(0, w - 1) * gap;
    const heightPx = pad * 2 + h * cell + Math.max(0, h - 1) * gap;

    const rows = chartObj?.rows || {};

    let svg = "";
    svg += `<?xml version="1.0" encoding="UTF-8"?>`;
    svg += `<svg xmlns="http://www.w3.org/2000/svg" width="${widthPx}" height="${heightPx}" viewBox="0 0 ${widthPx} ${heightPx}">`;
    svg += `<rect x="0" y="0" width="${widthPx}" height="${heightPx}" fill="white"/>`;

    // draw cells background and grid
    for (let r = 0; r < h; r += 1) {
      const rowKey = `row${r}`;
      const symbols = rows[rowKey] || [];
      for (let c = 0; c < w; c += 1) {
        const x = pad + c * (cell + gap);
        const y = pad + r * (cell + gap);
        svg += `<rect x="${x}" y="${y}" width="${cell}" height="${cell}" fill="#F4EFE8" stroke="#DAD4CB" stroke-width="0.5"/>`;
        const sym = (symbols[c] || "").toString();
        if (sym && sym !== "-") {
          const tx = x + cell / 2;
          const ty = y + cell / 2 + fontSize * 0.35;
          svg += `<text x="${tx}" y="${ty}" font-family="'Roboto Mono', monospace" font-size="${fontSize}" text-anchor="middle" fill="#2A2A2A">${sym.replace(/&/g, "&amp;").replace(/</g, "&lt;")}</text>`;
        }
      }
    }

    svg += `</svg>`;
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  }

  function getThumbnailDataUrl(row) {
    const raw = row.chart_json || "";
    if (!raw) return "";
    const hash = simpleHash(raw);
    const cacheKey = `lp_thumb_${row.id || row.pattern_name}_${hash}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) return cached;
    // decode chart
    let chartObj = null;
    try {
      const json = base64ToUtf8(raw);
      chartObj = JSON.parse(json);
    } catch (e) {
      return "";
    }
    const url = generateChartThumbnailSvg(chartObj, { maxSize: 192 });
    try {
      localStorage.setItem(cacheKey, url);
    } catch (e) {}
    return url;
  }

  function handleOpenExport(row) {
    try {
      const json = base64ToUtf8(row.chart_json || "");
      const chartObj = JSON.parse(json);
      const compiled = compilePatternToText(compilePattern(chartObj));
      const largeSvg = generateChartThumbnailSvg(chartObj, {
        maxSize: 1024,
        pad: 12,
        gap: 1,
        fontSize: 14,
      });
      setExportData({
        chart: chartObj,
        title: row.pattern_name || "Untitled Pattern",
        compiled,
        imageDataUrl: largeSvg,
      });
      setExportOpen(true);
    } catch (e) {
      setSnackbar({ open: true, message: "Sorry, this pattern could not be prepared for export.", severity: "error" });
    }
  }

  useEffect(() => {
    let didCancel = false;
    async function load() {
      setIsLoading(true);
      setErrorMessage("");
      try {
        const resp = await fetch(CSV_URL, { cache: "no-store" });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const text = await resp.text();
        const parsed = parseCsv(text);
        const sanitized = parsed.map((row) => {
          const {
            email, // eslint-disable-line no-unused-vars
            approval_status,
            visible,
            creator_name,
            pattern_name,
            ...rest
          } = row;

          return {
            id: row.id || "",
            pattern_name: pattern_name || "Untitled Pattern",
            creator_name: creator_name || "Anonymous",
            image_url: row.image_url || "",
            photo_url: row.photo_url || "",
            chart_json: row.chart_json || "",
            instructions: row.instructions || "",
            tags: row.tags || "",
            approval_status: approval_status || "",
            visible: (visible || "").toString().toLowerCase() === "true",
            created_at: row.created_at || "",
            notes: row.notes || "",
            // keep any additional fields
            ...rest,
          };
        });

        if (!didCancel) {
          setAllRows(sanitized);
          setIsLoading(false);
        }
      } catch (err) {
        if (!didCancel) {
          setErrorMessage(
            "Unable to load gallery data. Please refresh the page.",
          );
          setIsLoading(false);
        }
      }
    }
    load();
    return () => {
      didCancel = true;
    };
  }, []);

  const approvedRows = useMemo(
    () =>
      allRows.filter(
        (r) => (r.approval_status || "").toLowerCase() === "approved",
      ),
    [allRows],
  );

  const [searchInput, setSearchInput] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedQuery(searchInput.trim().toLowerCase());
    }, 250);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const allTags = useMemo(() => {
    const tagSet = new Set();
    approvedRows.forEach((r) => {
      (r.tags || "")
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean)
        .forEach((t) => tagSet.add(t));
    });
    return Array.from(tagSet).sort((a, b) => a.localeCompare(b));
  }, [approvedRows]);

  const filteredRows = useMemo(() => {
    const matchesQuery = (r) => {
      if (!debouncedQuery) return true;
      const q = debouncedQuery;
      const hay =
        `${r.pattern_name} ${r.creator_name} ${r.tags || ""}`.toLowerCase();
      return hay.includes(q);
    };
    const matchesTags = (r) => {
      if (selectedTags.length === 0) return true;
      const rowTags = (r.tags || "")
        .split(",")
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean);
      return selectedTags.every((t) => rowTags.includes(t.toLowerCase()));
    };
    return approvedRows.filter((r) => matchesQuery(r) && matchesTags(r));
  }, [approvedRows, debouncedQuery, selectedTags]);

  function toggleTag(tag) {
    setSelectedTags((prev) => {
      const has = prev.includes(tag);
      if (has) return prev.filter((t) => t !== tag);
      return [...prev, tag];
    });
  }

  function buildMailto(row) {
    const subject = `Reporting Pattern ${row.pattern_name}`;
    const body = [
      "Hello Hadley,",
      "",
      "I'd like to ask about or report this pattern:",
      "",
      `Pattern Name: ${row.pattern_name}`,
      `Pattern ID: ${row.id}`,
      "",
      "[ Your message here ]",
    ].join("\n");
    const enc = (s) => encodeURIComponent(s);
    return `mailto:hadleyswello@gmail.com?subject=${enc(subject)}&body=${enc(body)}`;
  }

  return (
    <>
      <SEO
        title="Pattern Gallery"
        description="Browse our community gallery of knitting patterns. Discover beautiful stitch charts created by knitters, filter by tags, and get inspired for your next project."
        keywords="knitting patterns gallery, stitch pattern collection, knitting inspiration, community patterns, free knitting patterns, pattern sharing, knitting charts gallery"
      />
      <Container maxWidth="lg" className="gallery-container">
      <Typography variant="h3" component="h1" gutterBottom>
        Community Gallery
      </Typography>

      {isLoading && (
        <Box className="gallery-placeholder">
          <Typography variant="h5" color="text.secondary">
            Loading gallery...
          </Typography>
        </Box>
      )}

      {!isLoading && errorMessage && (
        <Box className="gallery-placeholder">
          <Typography variant="h6" color="error">
            ⚠️ {errorMessage}
          </Typography>
        </Box>
      )}

      {!isLoading && !errorMessage && approvedRows.length === 0 && (
        <Box className="gallery-placeholder">
          <Typography variant="h5" color="text.secondary" gutterBottom>
            🧶 No patterns yet.
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Be the first to share your creation!
          </Typography>
        </Box>
      )}

      {!isLoading && !errorMessage && approvedRows.length > 0 && (
        <>
          <Box className="gallery-controls">
            <TextField
              fullWidth
              size="small"
              label="Search patterns"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label="Search patterns"
            />
            {allTags.length > 0 && (
              <Box
                className="gallery-tags"
                role="group"
                aria-label="Filter by tags"
              >
                {allTags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    onClick={() => toggleTag(tag)}
                    color={selectedTags.includes(tag) ? "secondary" : "default"}
                    variant={selectedTags.includes(tag) ? "filled" : "outlined"}
                    className="gallery-tag-chip"
                    aria-pressed={selectedTags.includes(tag)}
                  />
                ))}
              </Box>
            )}
          </Box>

          <Box className="gallery-summary">
            <Typography variant="body1" color="text.secondary">
              Showing {filteredRows.length} of {approvedRows.length} approved
              pattern{approvedRows.length === 1 ? "" : "s"}.
            </Typography>
          </Box>

          <Box className="gallery-grid">
            {filteredRows.map((row) => (
              <Card
                key={row.id || `${row.pattern_name}-${row.created_at}`}
                className="gallery-tile h-100"
                tabIndex={0}
                aria-label={`Pattern tile for ${row.pattern_name}`}
                elevation={1}
              >
                {row.chart_json ? (
                  <Box className="gallery-tile-image-wrap">
                    <CardMedia
                      component="img"
                      image={getThumbnailDataUrl(row) || row.image_url}
                      alt={`Chart preview for ${row.pattern_name}`}
                      loading="lazy"
                      className="gallery-tile-image"
                    />
                  </Box>
                ) : null}
                <CardContent className="gallery-tile-content">
                  <Typography variant="h6" className="gallery-tile-title">
                    {row.pattern_name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    className="gallery-tile-creator"
                  >
                    {row.creator_name || "Anonymous"}
                  </Typography>
                  <Box
                    className="gallery-tile-tags"
                    role="list"
                    aria-label="Pattern tags"
                  >
                    {(row.tags || "")
                      .split(",")
                      .map((t) => t.trim())
                      .filter(Boolean)
                      .slice(0, 4)
                      .map((t) => (
                        <Chip
                          key={t}
                          label={t}
                          size="small"
                          className="gallery-tile-tag"
                          role="listitem"
                        />
                      ))}
                  </Box>
                </CardContent>
                <CardActions className="gallery-tile-actions">
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    aria-label={`Export ${row.pattern_name}`}
                    onClick={() => handleOpenExport(row)}
                  >
                    Export
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    aria-label={`Edit ${row.pattern_name}`}
                    onClick={() => {
                      navigate("/chart-maker", {
                        state: {
                          chartJSONBase64: row.chart_json,
                          fromGallery: true,
                        },
                      });
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    aria-label={`Email about ${row.pattern_name}`}
                    onClick={() => {
                      window.location.href = buildMailto(row);
                    }}
                  >
                    Report
                  </Button>
                </CardActions>
              </Card>
            ))}
          </Box>
          <ExportModal
            open={exportOpen}
            onClose={() => setExportOpen(false)}
            chart={exportData.chart}
            patternTitle={exportData.title}
            compiledInstructions={exportData.compiled}
            chartImageDataUrl={exportData.imageDataUrl}
            attribution={
              exportData.title
                ? `Pattern by ${filteredRows.find((r) => (r.pattern_name || "") === exportData.title)?.creator_name || "Anonymous"} — via Loops & Purls Gallery.`
                : ""
            }
            hideImportSection
          />
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
    </>
  );
};

export default Gallery;
